import React, {useState, useEffect} from 'react';
import SMCModal from '../../components/core/SMCModal';
import SMCModalBody from '../../components/core/SMCModal/SMCModalBody'
import {useTranslation} from 'react-i18next';
import SMCModalButtonContainer from '../../components/core/SMCModal/SMCModalButtonContainer';
import SMCLabel from '../../components/Simple/SMCLabel';
import SMCTextInput from '../../components/Simple/SMCTextInput';
import SMCButton from '../../components/Simple/SMCButton';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import SMCPasswordInput from '../../components/Simple/SMCPasswordInput';
import {SMCCheckbox} from "../../components/Simple/SMCCheckbox";
import {validateEmailAddress, validatePhoneNumber} from "../../utils/utils";

function GatewayLocalUserEditorModal({
                                         isOpen = false,
                                         width = 610,
                                         height = 480,
                                         onCancel,
                                         onSuccess,
                                         defaultObject
                                     }) {
    const {t} = useTranslation();

    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [email, setEmail] = useState('');
    const [phoneNumber, setPhoneNumber] = useState('');
    const [comments, setComments] = useState('');
    const [isRemoteAccessUser, setIsRemoteAccessUser] = useState(false);

    const onFinish = () => {
        onSuccess({username, password, email, phoneNumber, comments, isRemoteAccessUser});
    }
    const _onCancel = () => {
        setIsUsernameValid(true);
        setIsPasswordValid(true);
        setIsConfirmPasswordValid(true);
        setIsEmailValid(true);
        setIsPhoneValid(true);
        onCancel();
    }

    useEffect(() => {
        if (isOpen) {
            setUsername(null);
            setPassword(null);
            setConfirmPassword(null);
            setEmail(null);
            setPhoneNumber(null);
            setComments(null);
            setIsRemoteAccessUser(false);

            if (defaultObject) {
                defaultObject.username && setUsername(defaultObject.username);
                defaultObject.password && setPassword(defaultObject.password);
                defaultObject.password && setConfirmPassword(defaultObject.password);
                defaultObject.email && setEmail(defaultObject.email);
                defaultObject.phoneNumber && setPhoneNumber(defaultObject.phoneNumber);
                defaultObject.comments && setComments(defaultObject.comments);
                defaultObject.isRemoteAccessUser && setIsRemoteAccessUser(defaultObject.isRemoteAccessUser);

                const isInitialFormValid = validateAllInitialValues(defaultObject);
                setIsFormValid(isInitialFormValid);
            } else {
                setIsFormValid(false);
            }
        }
        //eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, defaultObject]);

    //************ Validation methods ******************
    const usernameRegex = '^[a-zA-Z0-9_\\-\\.]{1,64}$';
    const passwordRegex = '^[a-zA-Z_][a-zA-Z0-9!@#\\$%\\^\\&amp;\\*\\(\\)\\?\\-_=\\+:;\\.\\,]{4,100}$';

    const validateUsername = (username) => {
        if (!username) {
            return false;
        }

        return new RegExp(usernameRegex).test(username);
    }

    const validatePassword = (password) => {
        if (!password) {
            return false;
        }

        return new RegExp(passwordRegex).test(password);
    }

    const validateConfirmPassword = (confirmPassword) => {
        return confirmPassword === password;
    };

    const validateEmail = (email) => {
        if (!email) {
            return true;
        }

        return validateEmailAddress(email);
    }

    const validatePhone = (phoneNumber) => {
        if (!phoneNumber) {
            return true;
        }

        return validatePhoneNumber(phoneNumber);
    };

    const validateAllInitialValues = ({ username, password, email, phoneNumber }) => {
        return validateUsername(username) &&
            validatePassword(password) &&
            validateEmail(email) &&
            validatePhone(phoneNumber);
    }

    const onPasswordChange = (e) => {
        const newPassword = e.target.value;
        const isConfirmPasswordValid = newPassword === confirmPassword;
        setIsConfirmPasswordValid(isConfirmPasswordValid)

        if (!isConfirmPasswordValid) {
            setIsFormValid(false)
        }
        setPassword(e.target.value);
    }

    //************ Validation callbacks ******************
    const [isUsernameValid, setIsUsernameValid] = useState(true);
    const [isPasswordValid, setIsPasswordValid] = useState(true);
    const [isConfirmPasswordValid, setIsConfirmPasswordValid] = useState(true);
    const [isEmailValid, setIsEmailValid] = useState(true);
    const [isPhoneValid, setIsPhoneValid] = useState(true);
    const [isFormValid, setIsFormValid] = useState(true);

    const usernameValidationCallback = (isValid) => {
        setIsUsernameValid(isValid);
    };

    const passwordValidationCallback = (isValid) => {
        setIsPasswordValid(isValid);
    };

    const confirmPasswordValidationCallback = (isValid) => {
        setIsConfirmPasswordValid(isValid);
    };

    const emailValidationCallback = (isValid) => {
        setIsEmailValid(isValid);
    };

    const phoneValidationCallback = (isValid) => {
        setIsPhoneValid(isValid);
    };

    useEffect(() => {
        const allFieldsAreValid = isUsernameValid && isPasswordValid && isConfirmPasswordValid && isEmailValid && isPhoneValid;

        setIsFormValid(allFieldsAreValid);
    }, [isUsernameValid, isPasswordValid, isConfirmPasswordValid, isEmailValid, isPhoneValid])

    return (
        <SMCModal
            isOpen={isOpen}
            width={width}
            height={height}
            title={t('SMP.PLANS.DEVICE_SETTINGS.LOCAL_USER.WIZARD.TITLE')}
            onRequestClose={_onCancel}
        >
            <SMCModalBody>
                <SMCLabel bold>{t('SMP.PLANS.DEVICE_SETTINGS.LOCAL_USER.WIZARD.TEXT')}</SMCLabel>
                <SMCTextInput
                    noAutoComplete
                    value={username}
                    onChange={e => setUsername(e.target.value)}
                    label={t('SMP.NAME')}
                    required={true}
                    validationFunc={validateUsername}
                    validationMsg={t('SMP.PLANS.DEVICE_SETTINGS.LOCAL_USER.WIZARD.INPUT_FIELDS.USERNAME.ERROR_MESSAGE')}
                    validationCallback={usernameValidationCallback}
                />
                <SMCPasswordInput
                    noAutoComplete
                    value={password}
                    onChange={onPasswordChange}
                    label={t('SMP.PASSWORD')}
                    allowPasswordPeek={false}
                    required={true}
                    validationFunc={validatePassword}
                    validationMsg={t('SMP.PLANS.DEVICE_SETTINGS.LOCAL_USER.WIZARD.INPUT_FIELDS.PASSWORD.ERROR_MESSAGE')}
                    validationCallback={passwordValidationCallback}
                />
                <SMCPasswordInput
                    noAutoComplete
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                    label={t('SMP.CONFIRM_PASSWORD')}
                    allowPasswordPeek={false}
                    required={true}
                    validationFunc={validateConfirmPassword}
                    validationMsg={t('SMP.PLANS.DEVICE_SETTINGS.LOCAL_USER.WIZARD.INPUT_FIELDS.CONFIRM_PASSWORD.ERROR_MESSAGE')}
                    validationCallback={confirmPasswordValidationCallback}
                    isValid={isConfirmPasswordValid}
                />
                <SMCTextInput
                    noAutoComplete
                    value={email}
                    onChange={e => setEmail(e.target.value? e.target.value : null)}
                    label={t('SMP.EMAIL')}
                    validationFunc={validateEmail}
                    validationMsg={t('SMP.PLANS.DEVICE_SETTINGS.LOCAL_USER.WIZARD.INPUT_FIELDS.EMAIL.ERROR_MESSAGE')}
                    validationCallback={emailValidationCallback}
                />
                <SMCTextInput
                    noAutoComplete
                    value={phoneNumber}
                    onChange={e => setPhoneNumber(e.target.value? e.target.value : null)}
                    label={t('SMP.PHONE')}
                    validationFunc={validatePhone}
                    validationMsg={t('SMP.PLANS.DEVICE_SETTINGS.LOCAL_USER.WIZARD.INPUT_FIELDS.PHONE.ERROR_MESSAGE')}
                    validationCallback={phoneValidationCallback}
                />
                <SMCTextInput
                    noAutoComplete
                    value={comments}
                    onChange={e => setComments(e.target.value? e.target.value : null)}
                    label={t('SMP.PLANS.DEVICE_SETTINGS.LOCAL_USER.WIZARD.INPUT_FIELDS.COMMENTS')}
                />
                <SMCCheckbox
                    checked={isRemoteAccessUser}
                    onChange={e => setIsRemoteAccessUser(e.target.checked)}
                    label={t('SMP.PLANS.DEVICE_SETTINGS.LOCAL_USER.WIZARD.INPUT_FIELDS.IS_REMOTE_ACCESS')}
                />
            </SMCModalBody>
            <SMCModalButtonContainer>
                <SMCButton onClick={onFinish} disabled={!isFormValid}>
                    <FontAwesomeIcon style={{marginTop: '3px', marginRight: '5px'}} color="green" icon="check"/>
                    {t('SMC_GEN_FINISH')}
                </SMCButton>
                <SMCButton onClick={_onCancel}>
                    <FontAwesomeIcon style={{marginTop: '3px', marginRight: '5px'}} color="grey" icon="times"/>
                    {t('SMC_GEN_CANCEL')}
                </SMCButton>
            </SMCModalButtonContainer>
        </SMCModal>

    );
}

export default GatewayLocalUserEditorModal;